import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  PlusIcon,
  TrashIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import { useDebounce } from '../hooks/useDebounce';
import { useSocket } from '../contexts/SocketContext';

const PAGE_SIZE = 50;
const FETCH_POLL_MS = 1500;

function fmt(n) {
  if (n == null || Number.isNaN(n)) return '—';
  return Number(n).toLocaleString();
}

function primaryLink(urls) {
  return Array.isArray(urls) && urls.length ? urls[0] : '';
}

export default function ScraperV2UsernamesTab({ canUseActiveCheckers = false }) {
  const { socket } = useSocket();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [groupFilter, setGroupFilter] = useState('');
  const [heldFilter, setHeldFilter] = useState(false);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [addToGroupId, setAddToGroupId] = useState('');
  const [fetchJob, setFetchJob] = useState(null);
  const [fetchStarting, setFetchStarting] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const syncedRef = useRef(false);
  const selectAllRef = useRef(null);

  const allSelected = total > 0 && selected.size === total;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = selected.size > 0 && !allSelected;
    }
  }, [selected.size, allSelected]);

  const matchingQuery = useCallback(() => {
    const params = new URLSearchParams({ keys: '1' });
    const query = String(debouncedSearch || '').trim();
    if (query) params.set('search', query);
    if (groupFilter) params.set('groupId', groupFilter);
    if (heldFilter) params.set('held', '1');
    return params;
  }, [debouncedSearch, groupFilter, heldFilter]);

  const selectionPayload = () => {
    if (allSelected) {
      return { allMatching: true, search: debouncedSearch, groupId: groupFilter, held: heldFilter ? '1' : '' };
    }
    return { usernames: [...selected] };
  };

  const loadGroups = useCallback(async () => {
    try {
      const res = await api.get('/scraper-v2/source-groups');
      if (res.data.success) setGroups(res.data.groups || []);
    } catch (_) { /* non-fatal */ }
  }, []);

  const loadRows = useCallback(async ({ page: p = 1, search: q = '', groupId = '', held = false, sync = false, silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(p),
        limit: String(PAGE_SIZE),
      });
      const query = String(q || '').trim();
      if (query) params.set('search', query);
      if (groupId) params.set('groupId', groupId);
      if (held) params.set('held', '1');
      if (sync) params.set('sync', '1');
      const res = await api.get(`/scraper-v2/source-usernames?${params}`);
      if (res.data.success) {
        setRows(res.data.items || []);
        setTotal(res.data.total || 0);
        setPage(res.data.page || p);
      }
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.error || 'Failed to load source usernames');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    const sync = !syncedRef.current;
    if (sync) syncedRef.current = true;
    loadRows({ page, search: debouncedSearch, groupId: groupFilter, held: heldFilter, sync });
  }, [page, debouncedSearch, groupFilter, heldFilter, loadRows]);

  useEffect(() => {
    if (!socket) return undefined;
    const onProfile = (payload) => {
      if (payload?.job) {
        setFetchJob((prev) => {
          if (!prev || prev.status !== 'running' || !payload.job.startedAt) return payload.job;
          if (prev.startedAt && payload.job.startedAt !== prev.startedAt) return payload.job;
          if ((prev.done || 0) > (payload.job.done || 0) && payload.job.status === 'running') return prev;
          return payload.job;
        });
      }
      const row = payload?.row;
      if (row?.username) {
        setRows((prev) => prev.map((r) => (
          r.username === row.username ? { ...r, ...row } : r
        )));
      }
    };
    socket.on('scraper-v2-source-profile', onProfile);
    return () => socket.off('scraper-v2-source-profile', onProfile);
  }, [socket]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/scraper-v2/source-usernames/fetch-profiles');
        if (!cancelled) setFetchJob(res.data.job || null);
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (fetchJob?.status !== 'running') return undefined;
    const tick = async () => {
      try {
        const res = await api.get('/scraper-v2/source-usernames/fetch-profiles');
        const job = res.data.job || null;
        setFetchJob((prev) => {
          if (!job) return prev;
          if (prev && (prev.done || 0) > (job.done || 0) && job.status === 'running') return prev;
          return job;
        });
      } catch (_) { /* ignore */ }
    };
    const t = setInterval(tick, FETCH_POLL_MS);
    return () => clearInterval(t);
  }, [fetchJob?.status]);

  const fetchStatus = fetchJob?.status;
  useEffect(() => {
    if (fetchStatus !== 'done') return undefined;
    loadRows({ page, search: debouncedSearch, groupId: groupFilter, held: heldFilter, silent: true });
    return undefined;
  }, [fetchStatus, page, debouncedSearch, groupFilter, loadRows]);

  const toggle = (username) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(username)) next.delete(username);
      else next.add(username);
      return next;
    });
  };

  const selectAllMatching = async (checked) => {
    if (!checked) {
      setSelected(new Set());
      return;
    }
    if (!total) return;
    setSelectingAll(true);
    try {
      const res = await api.get(`/scraper-v2/source-usernames?${matchingQuery()}`);
      const names = res.data.usernames || [];
      setSelected(new Set(names));
      toast.success(`Selected all ${fmt(names.length)} username(s)`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to select all usernames');
    } finally {
      setSelectingAll(false);
    }
  };

  const syncFromSessions = async () => {
    try {
      const res = await api.post('/scraper-v2/source-usernames/sync');
      if (res.data.success) {
        toast.success(`Synced ${res.data.synced || 0} source(s) from existing sessions`);
        setPage(1);
        await loadRows({ page: 1, search: debouncedSearch, groupId: groupFilter, held: heldFilter });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to sync source usernames');
    }
  };

  const deleteSelected = async () => {
    if (!selected.size) {
      toast.error('Select usernames to remove');
      return;
    }
    if (!window.confirm(
      `Remove ${selected.size} username(s) from global scrape history?\n\n`
      + 'They can be added to new sessions again. Follower data already collected is not deleted.'
    )) {
      return;
    }
    setDeleting(true);
    try {
      const res = await api.delete('/scraper-v2/source-usernames', {
        data: selectionPayload(),
      });
      if (res.data.success) {
        toast.success(res.data.message || 'Removed');
        setSelected(new Set());
        await loadRows({ page, search: debouncedSearch, groupId: groupFilter, held: heldFilter });
        await loadGroups();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove usernames');
    } finally {
      setDeleting(false);
    }
  };

  const createGroupFromSelection = async (e) => {
    e.preventDefault();
    if (!selected.size) {
      toast.error('Select usernames to group');
      return;
    }
    if (!newGroupName.trim()) return;
    setCreatingGroup(true);
    try {
      const res = await api.post('/scraper-v2/source-groups', {
        name: newGroupName.trim(),
        ...selectionPayload(),
      });
      if (res.data.success) {
        toast.success(`Group “${res.data.group.name}” created with ${selected.size} username(s)`);
        setGroupModalOpen(false);
        setNewGroupName('');
        setSelected(new Set());
        await loadGroups();
        await loadRows({ page, search: debouncedSearch, groupId: groupFilter, held: heldFilter });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create group');
    } finally {
      setCreatingGroup(false);
    }
  };

  const addSelectedToGroup = async () => {
    if (!selected.size) {
      toast.error('Select usernames first');
      return;
    }
    if (!addToGroupId) {
      toast.error('Pick a group');
      return;
    }
    try {
      const res = await api.patch(`/scraper-v2/source-groups/${addToGroupId}`, {
        addUsernames: allSelected ? undefined : [...selected],
        ...selectionPayload(),
      });
      if (res.data.success) {
        const name = groups.find((g) => g.id === addToGroupId)?.name || 'group';
        toast.success(`Added ${selected.size} to “${name}”`);
        setSelected(new Set());
        setAddToGroupId('');
        await loadGroups();
        await loadRows({ page, search: debouncedSearch, groupId: groupFilter, held: heldFilter });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add to group');
    }
  };

  const deleteGroup = async (group) => {
    if (!window.confirm(`Delete group “${group.name}”? Usernames stay in the list, they just leave the group.`)) {
      return;
    }
    try {
      await api.delete(`/scraper-v2/source-groups/${group.id}`);
      toast.success(`Deleted “${group.name}”`);
      if (groupFilter === group.id) setGroupFilter('');
      await loadGroups();
      await loadRows({ page, search: debouncedSearch, groupId: groupFilter === group.id ? '' : groupFilter, held: heldFilter });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete group');
    }
  };

  const startFetch = async ({ selectedOnly }) => {
    if (fetchJob?.status === 'running') {
      toast.error('A profile fetch is already running');
      return;
    }
    setFetchStarting(true);
    setFetchJob((prev) => (prev?.status === 'running' ? prev : {
      status: 'running',
      total: 0,
      done: 0,
      ok: 0,
      failed: 0,
      concurrency: 20,
      startedAt: new Date().toISOString(),
    }));
    try {
      const body = selectedOnly && selected.size
        ? (allSelected
          ? { force: true, allMissing: false, search: debouncedSearch, groupId: groupFilter }
          : { usernames: [...selected], force: true })
        : { allMissing: true, search: debouncedSearch, groupId: groupFilter };
      const res = await api.post('/scraper-v2/source-usernames/fetch-profiles', body);
      if (res.data.success) {
        setFetchJob(res.data.job);
        toast.success(res.data.message || 'Profile fetch started');
      }
    } catch (err) {
      setFetchJob(null);
      toast.error(err.response?.data?.error || 'Failed to start profile fetch');
    } finally {
      setFetchStarting(false);
    }
  };

  const fetching = fetchJob?.status === 'running' || fetchStarting;
  const fetchPct = fetchJob?.total
    ? Math.min(100, Math.round((fetchJob.done / fetchJob.total) * 100))
    : 0;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Source handles scraped across all Scraper V2 sessions. New sessions and add-sources skip these automatically.
        Removing a row clears global history only — follower data already stored stays in inventory.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-gray-500">Filter</span>
        <button
          type="button"
          onClick={() => { setHeldFilter((v) => !v); setPage(1); setSelected(new Set()); }}
          className={`rounded-full border px-2.5 py-1 text-xs ${
            heldFilter
              ? 'border-teal-400 bg-teal-50 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200'
              : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
          }`}
        >
          Held
        </button>
      </div>

      {groups.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-gray-500">Groups</span>
          {groups.map((g) => (
            <span
              key={g.id}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
                groupFilter === g.id
                  ? 'border-indigo-400 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
              }`}
            >
              <button type="button" onClick={() => { setGroupFilter(g.id); setPage(1); setSelected(new Set()); }}>
                {g.name} · {fmt(g.count)}
              </button>
              <button
                type="button"
                className="text-gray-400 hover:text-red-500"
                title={`Delete ${g.name}`}
                onClick={() => deleteGroup(g)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {fetching && (
        <div className="rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50/70 dark:bg-indigo-950/30 px-4 py-3">
          <div className="flex items-center justify-between text-sm text-indigo-800 dark:text-indigo-200 mb-2">
            <span>
              {fetchJob?.total
                ? `Fetching profiles · ${fmt(fetchJob.done)} / ${fmt(fetchJob.total)}`
                : 'Starting profile fetch…'}
              {fetchJob?.concurrency ? ` · ${fetchJob.concurrency} concurrent` : ''}
              {fetchJob?.failed ? ` · ${fetchJob.failed} failed` : ''}
            </span>
            <span>{fetchJob?.total ? `${fetchPct}%` : '…'}</span>
          </div>
          <div className="h-1.5 rounded-full bg-indigo-100 dark:bg-indigo-900 overflow-hidden">
            <div
              className="h-full bg-indigo-500 transition-all duration-300"
              style={{ width: fetchJob?.total ? `${fetchPct}%` : '8%' }}
            />
          </div>
        </div>
      )}

      {!fetching && fetchJob?.status === 'done' && (
        <p className="text-xs text-gray-500">
          Last fetch: {fmt(fetchJob.ok)} ok
          {fetchJob.failed ? `, ${fmt(fetchJob.failed)} failed` : ''}
          {' · '}{fmt(fetchJob.total)} total
        </p>
      )}

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
          <div>
            <span className="font-medium">Global source usernames</span>
            <p className="text-xs text-gray-500 mt-1">
              {fmt(total)} total
              {search.trim() ? ` · filtered by “${search.trim()}”` : ''}
              {selected.size ? ` · ${fmt(selected.size)} selected${allSelected ? ' (all)' : ''}` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
                setSelected(new Set());
              }}
              placeholder="Search @username, name, or link…"
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 min-w-[12rem]"
            />
            <select
              value={groupFilter}
              onChange={(e) => {
                setGroupFilter(e.target.value);
                setPage(1);
                setSelected(new Set());
              }}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
            >
              <option value="">All groups</option>
              <option value="ungrouped">Ungrouped</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
            <button
              type="button"
              disabled={!total || selectingAll || allSelected}
              onClick={() => selectAllMatching(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50"
            >
              {selectingAll ? 'Selecting…' : `Select all (${fmt(total)})`}
            </button>
            <button
              type="button"
              onClick={() => loadRows({ page, search: debouncedSearch, groupId: groupFilter, held: heldFilter })}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm"
            >
              <ArrowPathIcon className="w-4 h-4" /> Refresh
            </button>
            <button
              type="button"
              onClick={syncFromSessions}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm"
            >
              Sync from sessions
            </button>
            <button
              type="button"
              disabled={!selected.size || selectingAll}
              onClick={() => setGroupModalOpen(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50"
            >
              <UserGroupIcon className="w-4 h-4" />
              Create group ({selected.size})
            </button>
            <select
              value={addToGroupId}
              onChange={(e) => setAddToGroupId(e.target.value)}
              disabled={!selected.size || !groups.length}
              className="text-sm px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 disabled:opacity-50"
            >
              <option value="">Add to group…</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
            <button
              type="button"
              disabled={!selected.size || !addToGroupId}
              onClick={addSelectedToGroup}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50"
            >
              Add
            </button>
            <button
              type="button"
              disabled={fetchStarting || fetching || !selected.size || !canUseActiveCheckers}
              title={!canUseActiveCheckers ? 'Requires active checker access' : undefined}
              onClick={() => startFetch({ selectedOnly: true })}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-indigo-300 text-indigo-700 dark:text-indigo-300 text-sm disabled:opacity-50"
            >
              Fetch selected
            </button>
            <button
              type="button"
              disabled={fetchStarting || fetching || !canUseActiveCheckers}
              title={!canUseActiveCheckers ? 'Requires active checker access' : undefined}
              onClick={() => startFetch({ selectedOnly: false })}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm disabled:opacity-50"
            >
              Fetch missing profiles
            </button>
            <button
              type="button"
              disabled={deleting || !selected.size || selectingAll}
              onClick={deleteSelected}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-300 text-red-600 text-sm disabled:opacity-50"
            >
              <TrashIcon className="w-4 h-4" />
              {deleting ? 'Removing…' : `Remove selected (${selected.size})`}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/60 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={selectingAll || allSelected}
                      disabled={selectingAll || !total}
                      onChange={(e) => selectAllMatching(e.target.checked)}
                    />
                    {selectingAll ? 'Selecting…' : 'Select all'}
                  </label>
                </th>
                <th className="px-4 py-3">Profile</th>
                <th className="px-4 py-3">Username</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Bio</th>
                <th className="px-4 py-3">Link</th>
                <th className="px-4 py-3">Group</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Followers</th>
                <th className="px-4 py-3 text-right">Crawl rows</th>
                <th className="px-4 py-3 text-right">Ingested</th>
                <th className="px-4 py-3">Last scraped</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {loading && (
                <tr>
                  <td colSpan={12} className="px-4 py-8 text-center text-gray-500">Loading…</td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={12} className="px-4 py-8 text-center text-gray-500">
                    No source usernames yet. Create a session or sync from existing sessions.
                  </td>
                </tr>
              )}
              {!loading && rows.map((row) => {
                const link = primaryLink(row.profileUrls);
                return (
                  <tr key={row.username} className="hover:bg-gray-50/80 dark:hover:bg-gray-800/40 align-top">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectingAll || selected.has(row.username)}
                        onChange={() => toggle(row.username)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      {row.avatarUrl ? (
                        <img
                          src={row.avatarUrl}
                          alt=""
                          referrerPolicy="no-referrer"
                          className="h-9 w-9 rounded-full object-cover bg-gray-200"
                        />
                      ) : (
                        <div className="h-9 w-9 rounded-full bg-gray-200 dark:bg-gray-700" />
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      @{row.username}
                    </td>
                    <td className="px-4 py-3 text-gray-800 dark:text-gray-200 max-w-[10rem] truncate" title={row.displayName}>
                      {row.displayName || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-500 max-w-[16rem] truncate" title={row.bio}>
                      {row.bio || (row.profileFetchError ? (
                        <span className="text-red-500" title={row.profileFetchError}>error</span>
                      ) : '—')}
                    </td>
                    <td className="px-4 py-3 max-w-[12rem] truncate">
                      {link ? (
                        <a
                          href={link}
                          target="_blank"
                          rel="noreferrer"
                          className="text-indigo-600 dark:text-indigo-400 hover:underline"
                        >
                          {link.replace(/^https?:\/\//, '')}
                          {row.profileUrls.length > 1 ? ` +${row.profileUrls.length - 1}` : ''}
                        </a>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {(row.groups || []).length
                        ? row.groups.map((name) => (
                          <span
                            key={name}
                            className="inline-block mr-1 mb-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[11px] text-slate-600 dark:text-slate-300"
                          >
                            {name}
                          </span>
                        ))
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 capitalize text-gray-500">{row.lastStatus || '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmt(row.followersCount)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmt(row.rowsFetchedTotal)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmt(row.rawIngested)}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {row.lastScrapedAt ? new Date(row.lastScrapedAt).toLocaleString() : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {total > PAGE_SIZE && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between text-sm text-gray-500">
            <span>
              Page {page} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}
              {' · '}{fmt(total)} total
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1 rounded border disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={loading || page * PAGE_SIZE >= total}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 rounded border disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {groupModalOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
          <form
            onSubmit={createGroupFromSelection}
            className="w-full max-w-md rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-5 space-y-4"
          >
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Create group</h3>
            <p className="text-sm text-gray-500">
              {fmt(selected.size)} selected username{selected.size === 1 ? '' : 's'}
              {allSelected ? ' (every username in this list)' : ''} will be added to this group.
            </p>
            <input
              autoFocus
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="Group name"
              className="w-full text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setGroupModalOpen(false); setNewGroupName(''); }}
                className="px-3 py-1.5 rounded-lg border text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creatingGroup || !newGroupName.trim()}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm disabled:opacity-50"
              >
                <PlusIcon className="w-4 h-4" />
                {creatingGroup ? 'Creating…' : 'Create group'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
